import { useState, useCallback, useRef } from 'react';

// Hook personalizado para manejar swipe horizontal
const useSwipeHorizontal = (
  onSwipeLeft: () => void, 
  onSwipeRight: () => void,
  addDebugLog?: (message: string) => void
) => {
  const [swipeStartX, setSwipeStartX] = useState<number | null>(null);
  const [swipeStartY, setSwipeStartY] = useState<number | null>(null);
  const [swipeEndX, setSwipeEndX] = useState<number | null>(null);
  const [swipeEndY, setSwipeEndY] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isHorizontalSwipe, setIsHorizontalSwipe] = useState<boolean | null>(null);
  const touchStartTime = useRef<number>(0);

  const handleSwipeStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    console.log('🔄 [SWIPE] handleSwipeStart llamado');
    // addDebugLog?.('🔄 Swipe Start');
    // NO prevenir default aquí - esperar a determinar si es horizontal o vertical
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    console.log('🔄 [SWIPE] Posición inicial:', clientX);
    // addDebugLog?.(`Pos: ${clientX}`);
    setSwipeStartX(clientX);
    setSwipeStartY(clientY);
    setIsSwiping(true);
    setIsHorizontalSwipe(null); // Aún no sabemos la dirección
    touchStartTime.current = Date.now();
  }, []);

  const handleSwipeMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!isSwiping || swipeStartX === null || swipeStartY === null) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaX = Math.abs(clientX - swipeStartX);
    const deltaY = Math.abs(clientY - swipeStartY);
    
    // Determinar si es un movimiento horizontal o vertical
    // Si aún no se ha determinado, y el movimiento es claramente horizontal, prevenir default
    if (isHorizontalSwipe === null) {
      // Si el movimiento horizontal es mayor que el vertical por un factor de 2, es un swipe horizontal
      if (deltaX > deltaY * 2 && deltaX > 10) {
        setIsHorizontalSwipe(true);
        e.preventDefault(); // Solo prevenir default si es claramente horizontal
      } else if (deltaY > deltaX * 2 && deltaY > 10) {
        setIsHorizontalSwipe(false);
        // No prevenir default - permitir scroll vertical
        return;
      }
    } else if (isHorizontalSwipe === true) {
      // Si ya determinamos que es horizontal, prevenir default
      e.preventDefault();
    }
    // Si es vertical, no prevenir default y no actualizar estado
    
    setSwipeEndX(clientX);
    setSwipeEndY(clientY);
  }, [isSwiping, swipeStartX, swipeStartY, isHorizontalSwipe]);

  const handleSwipeEnd = useCallback((e?: React.TouchEvent | React.MouseEvent) => {
    console.log('🔄 [SWIPE] handleSwipeEnd llamado');
    // addDebugLog?.('🔄 Swipe End');
    // Solo prevenir default si fue un swipe horizontal confirmado
    if (e && isHorizontalSwipe === true) {
      e.preventDefault();
    }
    
    if (!isSwiping || swipeStartX === null || swipeEndX === null || swipeStartY === null || swipeEndY === null) {
      console.log('🔄 [SWIPE] Swipe cancelado - datos incompletos');
      // addDebugLog?.('❌ Datos incompletos');
      setIsSwiping(false);
      setSwipeStartX(null);
      setSwipeStartY(null);
      setSwipeEndX(null);
      setSwipeEndY(null);
      setIsHorizontalSwipe(null);
      return;
    }

    // Solo procesar si fue un swipe horizontal
    if (isHorizontalSwipe !== true) {
      // Fue un scroll vertical, limpiar y salir
      setIsSwiping(false);
      setSwipeStartX(null);
      setSwipeStartY(null);
      setSwipeEndX(null);
      setSwipeEndY(null);
      setIsHorizontalSwipe(null);
      return;
    }

    const swipeDistance = swipeEndX - swipeStartX;
    const swipeTime = Date.now() - touchStartTime.current;
    const minSwipeDistance = 80; // Aumentado para distinguir mejor de tap
    const maxSwipeTime = 600; // Reducido para ser más preciso
    const minSwipeTime = 50; // Tiempo mínimo para evitar taps accidentales

    console.log('🔄 [SWIPE] Distancia:', swipeDistance, 'Tiempo:', swipeTime, 'ms');
    // addDebugLog?.(`Dist: ${swipeDistance}px, Tiempo: ${swipeTime}ms`);

    // Verificar que sea un swipe válido (distancia y tiempo)
    if (Math.abs(swipeDistance) > minSwipeDistance && swipeTime > minSwipeTime && swipeTime < maxSwipeTime) {
      console.log('🔄 [SWIPE] Swipe válido detectado');
      // addDebugLog?.('✅ Swipe válido');
      if (swipeDistance > 0) {
        // Swipe hacia la derecha
        console.log('🔄 [SWIPE] Ejecutando onSwipeRight');
        // addDebugLog?.('➡️ Swipe Derecha');
        onSwipeRight();
      } else {
        // Swipe hacia la izquierda
        console.log('🔄 [SWIPE] Ejecutando onSwipeLeft');
        // addDebugLog?.('⬅️ Swipe Izquierda');
        onSwipeLeft();
      }
    } else {
      console.log('🔄 [SWIPE] Swipe no válido - distancia insuficiente o tiempo excedido');
      // addDebugLog?.('❌ Swipe inválido');
    }

    // Limpiar estado del swipe
    setIsSwiping(false);
    setSwipeStartX(null);
    setSwipeStartY(null);
    setSwipeEndX(null);
    setSwipeEndY(null);
    setIsHorizontalSwipe(null);
  }, [isSwiping, swipeStartX, swipeStartY, swipeEndX, swipeEndY, isHorizontalSwipe, onSwipeLeft, onSwipeRight]);

  const handleSwipeCancel = useCallback((e?: React.TouchEvent | React.MouseEvent) => {
    console.log('🔄 [SWIPE] handleSwipeCancel llamado');
    // No prevenir default en cancel - puede ser un scroll normal
    setIsSwiping(false);
    setSwipeStartX(null);
    setSwipeStartY(null);
    setSwipeEndX(null);
    setSwipeEndY(null);
    setIsHorizontalSwipe(null);
  }, []);

  return {
    handleSwipeStart,
    handleSwipeMove,
    handleSwipeEnd,
    handleSwipeCancel,
    isSwiping
  };
};

export default useSwipeHorizontal;
